import type { FilterSectionInitialLoadedEvent, FilterSectionLoadEvent, Page } from "./FilterTypes";
/*                                              */
import { FilterSectionWrapper } from "../filter/FilterSectionWrapper";
import type { Reloadable } from "../util/Reloadable";
import { Filter } from "../filter/Filter";
import { addSanErrorToPageImpression } from "../filter/FilterTracking";
import { events } from "../events";
import { forEachElement } from "../util/Utils";
import { isActive } from "../toggle/Toggle";

export class LoadFilter {
  private static readonly FILTERSECTION_URI: string = "/leafcutter/filtersection";
  private static readonly FILTER_SECTION_WRAPPER_CLASS: string = "heureka_filterSectionWrapper";
  private static readonly FILTER_SECTION_WRAPPER_SELECTOR: string = `.${LoadFilter.FILTER_SECTION_WRAPPER_CLASS}`;
  private static readonly FILTER_SECTION_WRAPPER_LOADING_CLASS: string = "heureka_filterSectionWrapper--loading";
  private static readonly OC_FILTER_ACCORDION_SELECTOR: string =
    ".heureka_filterAccordion.heureka_filterAccordion--regular";

  static async onLoadFilters({ rule, page, ssid }: FilterSectionLoadEvent): Promise<void> {
    try {
      const node: FilterSectionWrapper = await FilterSectionWrapper.template();
      const response: Response = await fetch(LoadFilter.uri(rule, LoadFilter.getEntryRule(rule), page), {
        headers: LoadFilter.requestHeaders(ssid),
      });
      const html: string = await LoadFilter.extractResponseText(response);
      return isActive("HEUREKA_1975_OC_COMPONENTS_MIGRATION")
        ? LoadFilter.replaceOcNodeContents(node, rule, html)
        : LoadFilter.replaceNodeContents(node, rule, html);
    } catch (e) {
      return LoadFilter.handleErrorReload(e as Error);
    }
  }

  static async onInitialLoadedFilters({ response, rule }: FilterSectionInitialLoadedEvent) {
    try {
      const node: FilterSectionWrapper = await FilterSectionWrapper.template();
      const html: string = await LoadFilter.extractResponseText(response);
      return LoadFilter.replaceNodeContents(node, rule, html, true);
    } catch (e) {
      return LoadFilter.handleErrorInitialLoad(e as Error);
    }
  }

  private static async extractResponseText(resp: Response) {
    if (resp.status !== 200) {
      throw Error(`Failed to retrieve filtersection: ${resp.status} ${resp.statusText}`);
    }
    return resp.text();
  }

  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  private static replaceNodeContents(reloadable: Reloadable, rule: string, html: string, initialLoad = false): void {
    /*                                 */
    const range = document.createRange();
    range.selectNodeContents(reloadable.elem);

    /*                                                               */
    const fragment = range.createContextualFragment(html);
    if (initialLoad && isActive("HEUREKA_1975_OC_COMPONENTS_MIGRATION")) {
      const wrapper = fragment.querySelector<HTMLElement>(LoadFilter.FILTER_SECTION_WRAPPER_SELECTOR);
      const productCount = LoadFilter.readProductCount(wrapper);
      events.filterSectionLoaded(rule, productCount);
    }
    /*                               */
    reloadable.prepare(fragment, initialLoad);

    /*           */
    range.deleteContents();
    range.insertNode(fragment);

    /*                     */
    reloadable.elem.dataset.rule = rule;

    /*                                                                       */
    window.invokePreload?.processPreloadsOnElement(reloadable.elem);
  }

  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  private static replaceOcNodeContents(reloadable: Reloadable, rule: string, html: string, initialLoad = false): void {
    const range = document.createRange();
    range.selectNodeContents(reloadable.elem);

    const fragment = range.createContextualFragment(html);
    const wrapper = fragment.querySelector<HTMLElement>(LoadFilter.FILTER_SECTION_WRAPPER_SELECTOR);
    const productCount = LoadFilter.readProductCount(wrapper);
    if (wrapper) {
      wrapper.classList.add(LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS);
    }
    reloadable.prepare(fragment, initialLoad);

    reloadable.elem.appendChild(fragment);

    /*                                                                            */
    const ocAccordionsInitialization = wrapper?.querySelector(LoadFilter.OC_FILTER_ACCORDION_SELECTOR);

    /*                                                                               */
    if (!ocAccordionsInitialization) {
      console.warn("No filter accordions found on filters reload");
      LoadFilter.removeOldFilterSectionWrappers();
      events.filterSectionLoaded(rule, productCount);
      wrapper?.classList.remove(LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS);
    } else {
      /*                                                                     */
      /*                                                                                         */
      ocAccordionsInitialization?.addEventListener(
        "oc-mount",
        () => {
          LoadFilter.removeOldFilterSectionWrappers();
          events.filterSectionLoaded(rule, productCount);
          wrapper!.classList.remove(LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS);
        },
        { once: true },
      );
    }

    /*                     */
    reloadable.elem.dataset.rule = rule;

    /*                                                                       */
    window.invokePreload?.processPreloadsOnElement(reloadable.elem);
  }

  private static removeOldFilterSectionWrappers() {
    forEachElement<HTMLElement>(
      `${LoadFilter.FILTER_SECTION_WRAPPER_SELECTOR}:not(.${LoadFilter.FILTER_SECTION_WRAPPER_LOADING_CLASS})`,
      (wrapper) => {
        wrapper.remove();
      },
    );
  }

  private static readProductCount(wrapper?: HTMLElement | null) {
    const productCountElement = wrapper?.querySelector<HTMLElement>(".heureka_menu-bar-button [data-product-count]");
    return productCountElement?.dataset.productCount ? Number(productCountElement.dataset.productCount) : undefined;
  }

  private static handleErrorReload(e: Error): void {
    console.warn(`Filter reload encountered an error: ${e.message}`);
    Filter.handleError();
    addSanErrorToPageImpression("filterlist_notloaded");
    events.filterSectionLoadAborted();
  }

  private static handleErrorInitialLoad(e: Error): void {
    console.warn(`Filter reload encountered an error: ${e.message}`);
    addSanErrorToPageImpression("filterlistInitial_notloaded");
    events.filterSectionLoadAborted();
  }

  private static uri(currentRule: string, entryRule: string, page: Page) {
    let url = `${LoadFilter.FILTERSECTION_URI}?rule=${encodeURIComponent(currentRule)}&entryRule=${encodeURIComponent(entryRule)}`;
    if (page.sortiertnach) {
      url = `${url}&sortiertnach=${encodeURIComponent(page.sortiertnach)}`;
    }
    return url;
  }

  private static requestHeaders(ssid?: string): HeadersInit | undefined {
    return ssid ? { "X-Heureka-SSID": ssid } : undefined;
  }

  private static getEntryRule(fallbackRule: string) {
    return (window.history && window.history.state && window.history.state.entryRule) || fallbackRule;
  }
}
